import React, { useEffect, useState, useMemo } from "react";
import { IoArrowBack, IoChevronBack, IoChevronForward } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import "./BookingAppointmentCss/DateAndTimeSection.css";
import { getBookedSlots, getScheduleDates } from "../../api/api";

import {
  calculateSlots,
  formatScheduleDate,
  groupSlotsByPeriod,
} from "../../utils/helper";
import CustomLoader from "../../common/loader/CustomLoader";

export default function DateAndTimeSection({
  onNext,
  onBack,
  doctorData,
  handleTimeClick,
  selectedTime,
  onDateChange,
  selectedCenter,
  setOpdTiming,
} = {}) {
  // console.log("doctor data in date and time >>", doctorData);

  const today = new Date();
  const navigator = useNavigate();

  const [selectedDate, setSelectedDate] = useState(null);

  const [startIndex, setStartIndex] = useState(0);
  const [scheduledDates, setScheduledDates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [timeSlots, setTimeSlots] = useState({});

  const sortedDates = useMemo(
    () =>
      scheduledDates.sort((a, b) => {
        const [da, ma, ya] = a.VDATE.split("-").map(Number);
        const [db, mb, yb] = b.VDATE.split("-").map(Number);

        const dateA = new Date(ya, ma - 1, da);
        const dateB = new Date(yb, mb - 1, db);

        return dateA - dateB;
      }),
    [scheduledDates]
  );

  const visibleCount = 5;

  // 👉 fetch API
  useEffect(() => {
    if (Array.isArray(doctorData) && doctorData.length > 0) {
      fetchScheduledDates(doctorData[0]);
    }
  }, [doctorData, selectedCenter]);

  useEffect(() => {
    if (selectedDate) {
      onDateChange?.(selectedDate);
    }
  }, [selectedDate]);

  const fetchScheduledDates = async (doctor) => {
    const today = new Date();
    const nextMonth = new Date();
    nextMonth.setMonth(today.getMonth() + 1);

    const payload = {
      RBAuthKey: "RBDWAh!Q1s74e",
      //   center_id: doctor?.center_id || "",
      doctor_id: doctor?.doctor_id || "",
      center_id: selectedCenter?.center_id,
      From_Date: formatScheduleDate(today),
      To_Date: formatScheduleDate(nextMonth),
    };

    try {
      setLoading(true);
      const response = await getScheduleDates(payload);
      const data = response?.data?.data || [];
      setScheduledDates(data);
      setOpdTiming(data[0]?.OPDTimingID || "");
    } catch (error) {
      console.error("Error fetching scheduled dates >>", error);
    } finally {
      setLoading(false);
    }
  }; 

  //   const fetchBookedSlots = async (date, opdTimingId, generatedSlots) => {
  //     const payload = {
  //       RBAuthKey: "RBDWAh!Q1s74e",
  //       AppDate: date, // 👈 use VDATE
  //       OPDTimingID: opdTimingId, // 👈 from schedule API
  //     };

  //     try {
  //       setLoading(true);
  //       const response = await getBookedSlots(payload);
  //       const booked = response?.data?.data || [];

  //       const grouped = groupSlotsByPeriod(generatedSlots, booked);

  //       setTimeSlots(grouped);
  //     } catch (error) {
  //       console.error("Error fetching booked slots >>", error);
  //     } finally {
  //       setLoading(false);
  //     }
  //   };

  // 👉 when scheduledDates updates, auto-select today's date (or first available)

  // 🔍 Helper: Check if a time slot is in the past
  const isTimeInPast = (timeStr, compareDate) => {
    const now = new Date();
    const selectedDateObj = new Date();

    // Parse date string (format: DD-MM-YYYY)
    const [day, month, year] = compareDate.split("-").map(Number);
    selectedDateObj.setFullYear(year, month - 1, day);

    // If comparing to a past date, all times are expired
    if (selectedDateObj < new Date(now.getFullYear(), now.getMonth(), now.getDate())) {
      return true;
    }

    // If not today, don't disable (future dates)
    if (selectedDateObj.toDateString() !== now.toDateString()) {
      return false;
    }

    // Today: compare times
    // Parse time string (handles both "06:00 PM" and "18:00" formats)
    let slotHour, slotMin;
    
    if (timeStr.includes("AM") || timeStr.includes("PM")) {
      // 12-hour format with AM/PM (e.g., "06:00 PM")
      const timeWithoutAmPm = timeStr.replace(/\s*(AM|PM)/i, "").trim();
      const timeParts = timeWithoutAmPm.split(":").map(Number);
      slotHour = timeParts[0];
      slotMin = timeParts[1];
      
      const isPM = /PM/i.test(timeStr);
      
      // Convert to 24-hour format
      if (isPM && slotHour !== 12) {
        slotHour += 12;
      } else if (!isPM && slotHour === 12) {
        slotHour = 0;
      }
    } else {
      // 24-hour format (e.g., "18:00")
      const timeParts = timeStr.split(":").map(Number);
      slotHour = timeParts[0];
      slotMin = timeParts[1];
    }

    const currentHour = now.getHours();
    const currentMin = now.getMinutes();

    return slotHour < currentHour || (slotHour === currentHour && slotMin <= currentMin);
  };

  const fetchBookedSlots = async (date, opdTimingId, generatedSlots) => {
    const payload = {
      RBAuthKey: "RBDWAh!Q1s74e",
      AppDate: date,
      OPDTimingID: opdTimingId,
    };

    try {
      setLoading(true);
      const response = await getBookedSlots(payload);
      const booked = response?.data?.data || [];

      let bookedTimes = booked.map((b) => b.AppTime);

      let finalSlots = generatedSlots.map((slot) => ({
        label: slot,
        booked: bookedTimes.includes(slot),
        pastTime: isTimeInPast(slot, date), // 🆕 Check if time is in past
        disabled: true, // disable by default
      }));

      if (bookedTimes.length === 0) {
        // ✅ case: no bookings → enable first 4 non-past slots
        let enabledCount = 0;
        for (let i = 0; i < finalSlots.length && enabledCount < 4; i++) {
          if (!finalSlots[i].pastTime) {
            finalSlots[i].disabled = false;
            enabledCount++;
          }
        }
      } else {
        // ✅ case: has bookings → rolling 4-slot logic
        // find the first not-booked and not-past slot
        let startIndex = finalSlots.findIndex((s) => !s.booked && !s.pastTime);

        if (startIndex !== -1) {
          let enabledCount = 0;
          let idx = startIndex;

          while (enabledCount < 4 && idx < finalSlots.length) {
            if (!finalSlots[idx].booked && !finalSlots[idx].pastTime) {
              finalSlots[idx].disabled = false;
              enabledCount++;
            }
            idx++;
          }
        }
      }

      // group and set state
      const grouped = groupSlotsByPeriod(finalSlots);
      setTimeSlots(grouped);
    } catch (error) {
      console.error("Error fetching booked slots >>", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (scheduledDates.length > 0) {
      const todayStr = formatScheduleDate(new Date());
      const schedule = scheduledDates.find((d) => d.VDATE === todayStr);

      const target = schedule || scheduledDates[0]; // fallback

      setSelectedDate(target.VDATE);
      //   setSelectedTime(null);

      const slots = calculateSlots(
        target.FromTime,
        target.ToTime,
        Number(target.PatientTimeSlot)
      );

      // call booked API for first render
      fetchBookedSlots(target.VDATE, target.OPDTimingID, slots);
    }
  }, [scheduledDates]);

  const handleDateClick = (date) => {
    setSelectedDate(date);
    // setSelectedTime(null);

    const schedule = scheduledDates.find((d) => d.VDATE === date);
    if (schedule) {
      const slots = calculateSlots(
        schedule.FromTime,
        schedule.ToTime,
        Number(schedule.PatientTimeSlot)
      );

      // call booked API for clicked date
      fetchBookedSlots(schedule.VDATE, schedule.OPDTimingID, slots);
    }
  };

  //   const handleTimeClick = (time) => setSelectedTime(time);

  const handleBackBtn = (e) => {
    e.preventDefault();
    navigator("/doctors-grid");
  };

  // 👉 Carousel logic
  const handlePrev = () => {
    if (startIndex > 0) setStartIndex(startIndex - 1);
  };

  const handleNext = () => {
    if (startIndex < scheduledDates.length - visibleCount) {
      setStartIndex(startIndex + 1);
    }
  };

  return (
    <>
      {loading && <CustomLoader />}
      <div className="step1-container">
        {/* ✅ Date Carousel */}
        <div className="date-slider-wrapper">
          <button
            className="nav-btn-date"
          onClick={handlePrev}
          disabled={startIndex === 0}
        >
          <IoChevronBack />
        </button>

        <div className="date-slider-container">
          <div
            className="date-slider-track"
            style={{ transform: `translateX(-${startIndex * 125}px)` }}
          >
            {sortedDates.map((d, i) => (
              <button
                key={i}
                className={`date-btn ${
                  selectedDate === d.VDATE ? "active" : ""
                }`}
                onClick={() => handleDateClick(d.VDATE)}
              >
                <div className="date-label">{d.VDAY}</div>
                <div className="date-text">{d.VDATE}</div>
              </button>
            ))}
          </div>
        </div>

        <button
          className="nav-btn-date"
          onClick={handleNext}
          disabled={startIndex >= scheduledDates.length - visibleCount}
        >
          <IoChevronForward />
        </button>
      </div>

      {/* ✅ Time Slots */}
      <div className="step1-time-slots">
        {Object.entries(timeSlots).map(
          ([period, times]) =>
            times.length > 0 && (
              <div key={period}>
                {/* <p className="step1-period-label">{period} Slots</p> */}
                <div className="slots-grid">
                  {times.map((time, i) => (
                    <button
                      key={i}
                      disabled={time.booked || time.disabled || time.pastTime}
                      className={`slot-btn
    ${time.booked ? "booked" : ""}
    ${time.pastTime ? "past-time" : ""}
    ${time.available ? "available" : ""}
    ${time.disabled && !time.booked && !time.pastTime ? "disabled" : ""}
    ${selectedTime === time.label ? "selected" : ""}
  `}
                      onClick={() =>
                        !time.booked &&
                        !time.disabled &&
                        !time.pastTime &&
                        handleTimeClick(time.label)
                      }
                      title={time.pastTime ? "This time has already passed" : ""}
                    >
                      {time.label}
                      {time.booked && " (Booked)"}
                      {/* {time.pastTime && " (Expired)"} */}
                    </button>
                  ))}
                </div>
              </div>
            )
        )}
      </div>

      {/* ✅ Footer */}
      <div className="step1-footer-buttons">
        <button
          className="step1-back-btn rbdButton reset-Btn reportBtn"
          onClick={onBack}
        >
          <span className="btn-text" onClick={handleBackBtn}>
            <IoArrowBack className="step1-back-icon " /> Back
          </span>
        </button>

        <button
          className="step1-next-btn rbdButton"
          disabled={!selectedTime}
          onClick={() => onNext?.(selectedDate, selectedTime)}
        >
          <span className="btn-text">Add Basic Information</span>
        </button>
      </div>
    </div>
    </>
  );
}
